import { SxProps, Theme } from '@mui/material';
import React, { ReactElement, ReactNode } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';
import { hexRegex, rgbRgbaRegex } from 'src/constants/validations';
import { ColorInput } from '../Inputs/ColorInput';

interface FormColorInputProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  fieldName: Path<T>;

  label?: ReactNode;
  placeholder?: string;
  helperText?: ReactNode;
  validation?: RegisterOptions;

  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  variant?: "outlined" | "filled" | "standard";
  margin?: "dense" | "normal" | "none";
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

export const FormColorInput = <T extends FieldValues>(props: FormColorInputProps<T>): ReactElement | null => {
  const {
    form,
    fieldName,
    label,
    placeholder,
    helperText,
    validation,
    disabled,
    variant = "outlined",
    margin = "normal",
    fullWidth = true,
    sx,
  } = props;

  return (
    <Controller
      name={fieldName}
      control={form.control as Control<FieldValues>}
      rules={{
        ...validation,
        validate: (color) => {
          if (hexRegex.test(color) || rgbRgbaRegex.test(color)) {
            return true;
          }

          return "Invalid color";
        },
      }}
      render={({ field, fieldState }) => {
        const { value, onChange, ...rest } = field;
        const { error } = fieldState;

        return (
          <ColorInput
            {...rest}
            label={label}
            placeholder={placeholder}
            value={value}
            onChange={(color) => onChange(color)}
            error={!!error}
            helperText={error?.message || helperText}
            required={!!validation?.required}
            disabled={disabled}
            variant={variant}
            fullWidth={fullWidth}
            margin={margin}
            sx={sx}
          />
        );
      }}
    />
  );
};
